import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useToken } from './TokenContext';

export function UserEditForm() {
    const [loading, setLoading] = useState(true);
    const [loadingForm, setLoadingForm] = useState(false);
    const [userData, setUserData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        type: 'normal',
    });

    const [error, setError] = useState('');
    const { tokenData } = useToken();
    const navigate = useNavigate();
    const { id } = useParams();
    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        if (tokenData.data.type === 'normal') {
            navigate('/');
        }
    }, [navigate, tokenData]);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await fetch(`${baseUrl}/users/${id}`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                });
                if (!response.ok) throw new Error('Erro ao buscar os dados do usuário.');
                const fetchedData = await response.json();

                if (!fetchedData.data) {
                    navigate('/usuarios');
                };

                setUserData({
                    ...userData,
                    name: fetchedData.data.name,
                    email: fetchedData.data.email,
                    type: fetchedData.data.type,
                });
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };
        fetchUser();
    }, [id, baseUrl, accessToken]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserData({ ...userData, [name]: value });
    };

    const validateForm = () => {
        if (!userData.name || !userData.email || !userData.password || !userData.confirmPassword) {
            setError('Todos os campos são obrigatórios');
            return false;
        }
        if (userData.password !== userData.confirmPassword) {
            setError('As senhas não coincidem');
            return false;
        }
        if (!/\S+@\S+\.\S+/.test(userData.email)) {
            setError('E-mail inválido');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoadingForm(true);

        if (!validateForm()) {
            setLoadingForm(false);
            return;
        };

        try {
            const response = await fetch(`${baseUrl}/users/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify(userData)
            });
            if (response.ok) {
                setLoadingForm(false);
                navigate('/usuarios', { state: { message: { type: 'success', text: 'Usuário atualizado com sucesso!' } } });
            } else {
                setLoadingForm(false);
                setError('Erro ao atualizar o usuário');
            }
        } catch (error) {
            setLoadingForm(false);
            setError(error.message);
        }
    };

    if (loading) return (
        <div className="mx-auto py-10">
            <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
        </div>
    );

    return (
        <>
            <form onSubmit={handleSubmit} className="max-w-4xl pb-10">
                <div className="mb-5 flex gap-5 lg:gap-15 flex-col lg:flex-row">
                    <div className="w-full">
                        <label className="block text-sm font-medium">Nome</label>
                        <input
                            type="text"
                            name="name"
                            value={userData.name}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                        />
                    </div>
                </div>

                <div className="mb-5 flex gap-5 lg:gap-15 flex-col lg:flex-row">
                    <div className="w-full">
                        <label className="block text-sm font-medium">E-mail</label>
                        <input
                            type="email"
                            name="email"
                            value={userData.email}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                        />
                    </div>

                    <div className="w-full">
                        <label className="block text-sm font-medium">Tipo de Usuário</label>
                        <select
                            name="type"
                            value={userData.type}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="admin">Administrador</option>
                            <option value="normal">Colaborador</option>
                            <option value="supplier">Fornecedor</option>
                        </select>
                    </div>
                </div>

                <div className="mb-5 flex gap-5 lg:gap-15 flex-col lg:flex-row">
                    <div className="w-full">
                        <label className="block text-sm font-medium">Senha</label>
                        <input
                            type="password"
                            name="password"
                            value={userData.password}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                        />
                    </div>
                    
                    <div className="w-full">
                        <label className="block text-sm font-medium">Repetir Senha</label>
                        <input
                            type="password"
                            name="confirmPassword"
                            value={userData.confirmPassword}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    className="w-full bg-secondary text-white py-2 px-4 rounded-md hover:bg-slate-700"
                >
                    {!loadingForm ? (
                        'Salvar'
                    ) : (
                        <div role="status" className="flex justify-center items-center">
                            <svg aria-hidden="true" className="w-6 h-6 text-white text-opacity-60 animate-spin fill-white" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                            </svg>
                            <span className="sr-only">Loading...</span>
                        </div>
                    )}
                </button>
                {error && <div className="text-red-600 mt-4 -mb-4">{error}</div>}
            </form>
        </>
    );
}
